import { Component } from '@theme/component';
import { CartLinesUpdateEvent, CartErrorEvent } from '@shopify/events';
import { groupCartByCasepack, setCasepackPackCount } from '@theme/casepack';

/**
 * @module casepack-cart
 *
 * Cart-side UI for the casepack build (phase 3). The cart Liquid collapses all
 * member lines of one casepack (same product + color, tagged with the shared
 * `_casepack` line property) into a single visual row. This component powers
 * that row's controls:
 *
 * - plus / minus step the WHOLE casepack (every member line scales together)
 * - the input shows the pack count, not units
 * - remove deletes every member line of the casepack in one cart write
 *
 * The backend cart is untouched conceptually: the real per-size lines stay in
 * the cart exactly as the integration expects; only the presentation groups
 * them. All pack math and the `/cart/update.js` write live in `@theme/casepack`
 * (`groupCartByCasepack` / `setCasepackPackCount`); this component is DOM and
 * event glue. After a write it resolves a `CartLinesUpdateEvent` carrying the
 * re-rendered section HTML, which `cart-items-component` picks up and morphs —
 * the same refresh path the stock quantity selector uses.
 */

/**
 * @typedef {Object} Refs
 * @property {HTMLInputElement} quantityInput
 * @property {HTMLButtonElement} minusButton
 * @property {HTMLButtonElement} plusButton
 * @extends {Component<Refs>}
 */
class CasepackCartLine extends Component {
  requiredRefs = ['quantityInput', 'minusButton', 'plusButton'];

  #busy = false;

  increasePack() {
    this.#step(1);
  }

  decreasePack() {
    this.#step(-1);
  }

  removePack() {
    this.#setPacks(0);
  }

  /** @param {number} delta */
  #step(delta) {
    const current = Number(this.dataset.packCount) || Number(this.refs.quantityInput.value) || 1;
    const target = Math.max(0, current + delta);
    if (target === current) return;
    this.#setPacks(target);
  }

  /**
   * Rewrite every member line of this casepack to `target` whole packs
   * (0 removes the casepack), then hand the cart components the re-rendered
   * section HTML via the standard cart update event.
   * @param {number} target
   */
  async #setPacks(target) {
    if (this.#busy) return;
    this.#busy = true;
    this.#setDisabled(true);

    try {
      const response = await fetch(`${Theme.routes.cart_url}.js`, {
        headers: { Accept: 'application/json' },
        credentials: 'same-origin',
      });
      if (!response.ok) throw new Error(`Failed to fetch cart: ${response.status}`);
      const cartData = await response.json();

      const { groups } = groupCartByCasepack(cartData.items);
      const deliveryDate = this.dataset.deliveryDate || '';
      const group = groups.find(
        (candidate) => candidate.key === this.dataset.casepackKey && candidate.deliveryDate === deliveryDate
      );
      if (!group || !group.consistent) {
        console.warn('[casepack] cart line group missing or inconsistent:', this.dataset.casepackKey);
        return;
      }

      const sections = Array.from(document.querySelectorAll('cart-items-component'))
        .map((element) => /** @type {HTMLElement} */ (element).dataset.sectionId)
        .filter(Boolean)
        .join(',');

      const deferred = CartLinesUpdateEvent.createPromise();
      this.dispatchEvent(
        new CartLinesUpdateEvent({
          action: target === 0 ? 'remove' : 'update',
          context: 'cart',
          lines: group.lines.map((line) => ({
            id: String(line.key),
            quantity: target === 0 ? 0 : Math.round((Number(line.quantity) / group.packCount) * target),
          })),
          promise: deferred.promise,
        })
      );

      try {
        const updated = await setCasepackPackCount(group, target, sections ? { sections } : {});
        deferred.resolve({
          cart: CartLinesUpdateEvent.createCartFromAjaxResponse(updated),
          detail: {
            sections: updated.sections,
            items: updated.items,
            itemCount: updated.item_count,
            source: 'casepack-cart-line',
            didError: false,
          },
        });
      } catch (error) {
        const message =
          /** @type {any} */ (error)?.cartError?.description || /** @type {any} */ (error)?.message || 'Cart update failed';
        deferred.reject(error);
        this.dispatchEvent(
          new CartErrorEvent({
            error: message,
            code: /** @type {any} */ (error)?.cartError ? 'INVALID' : 'SERVICE_UNAVAILABLE',
          })
        );
        console.warn('[casepack] cart update failed:', message);
      }
    } catch (error) {
      console.warn('[casepack] could not update casepack line:', error);
    } finally {
      this.#busy = false;
      this.#setDisabled(false);
    }
  }

  /** @param {boolean} disabled */
  #setDisabled(disabled) {
    const { quantityInput, minusButton, plusButton } = this.refs;
    quantityInput.disabled = disabled;
    minusButton.disabled = disabled;
    plusButton.disabled = disabled;
  }
}

if (!customElements.get('casepack-cart-line')) {
  customElements.define('casepack-cart-line', CasepackCartLine);
}
